import { useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { clone, orderBy, uniqBy } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  type GeneralChartSelectorType,
  useModelInsightsKpiGeneralChartIds,
  useModelInsightsKpiGeneralChartSelector,
} from "@features/mmm/hooks/mmm-url-state";
import { formatting } from "@north-beam/nb-common";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { TertiaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";

import { type GraphMetadata } from "./general-insights-graph";

function getMetricName(
  selectValue: GeneralChartSelectorType,
  isRevenue: boolean,
) {
  if (selectValue === "roas") {
    return isRevenue ? "iROAS" : "iCAC";
  }

  if (selectValue === "spend") {
    return "Spend";
  }
  return "KPI";
}

export function ChartSplitLegend({
  datasets,
  isRevenue,
}: {
  datasets: { metadata: GraphMetadata; id: string }[];
  isRevenue: boolean;
}) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const { user } = useUser();
  const [chartIds, setChartIds] = useModelInsightsKpiGeneralChartIds();
  const [showAll, setShowAll] = useState(false);

  const [gridColCount, setGridColCount] = useState(0);
  const gridRef = useRef(null);
  useEffect(() => {
    const gridEl = gridRef.current;
    if (gridEl) {
      const gridColStyle = window
        .getComputedStyle(gridEl)
        .getPropertyValue("grid-template-columns");
      setGridColCount(gridColStyle.split(" ").length);
    }
  }, [gridRef]);

  const chartIdsSet = useMemo(() => new Set(chartIds), [chartIds]);

  const [selectValue] = useModelInsightsKpiGeneralChartSelector();

  // HACK: This is a hack to force the legend to re-render when the chart is updated
  const [HACK, setHACK] = useState(0);
  useEffect(() => {
    if (datasets.length !== 0 && datasets.length < 2) {
      setHACK((prev) => prev + 1);
    }
  }, [HACK, datasets]);

  const splitLegend = useMemo(() => {
    return uniqBy(
      orderBy(datasets, "metadata.orderingTotal", "desc").map(
        ({ metadata, id }) => ({
          breakdowns: metadata.breakdowns,
          color: metadata.color,
          id,
          total: metadata.total,
        }),
      ),
      (e) => Object.values(e.breakdowns).join(""),
    );
  }, [datasets]);

  return (
    <div className="flex items-center space-x-20 text-xs ml-8">
      <div className="font-semibold whitespace-nowrap mb-2 -ml-6">
        {`Incremental ${getMetricName(selectValue, isRevenue)}`}
      </div>
      <div
        ref={gridRef}
        className={clsx(
          "grid gap-3 py-1.5 grid-cols-[repeat(auto-fit,minmax(170px,1fr))] w-full ",
          { "h-10 overflow-hidden": !showAll },
        )}
      >
        {splitLegend.map((legend, index) => {
          const value =
            selectValue === "spend"
              ? formatting.formatCurrency(legend.total, {
                  code: user.currencies.currentCurrency,
                  precision: "approximate",
                })
              : selectValue === "roas" && isRevenue
              ? formatting.formatMultiplier(legend.total, {
                  precision: "approximate",
                })
              : selectValue === "roas" && !isRevenue
              ? formatting.formatCurrency(legend.total, {
                  code: user.currencies.currentCurrency,
                  precision: "approximate",
                })
              : isRevenue
              ? formatting.formatCurrency(legend.total, {
                  code: user.currencies.currentCurrency,
                  precision: "approximate",
                })
              : formatting.formatDecimal(legend.total, {
                  precision: "approximate",
                });

          const label =
            Object.values(legend.breakdowns).join(" / ") ||
            `(Misconfigured) ${legend.id}`;

          return (
            <GenericTooltip
              key={index}
              noInfoCircle
              content={
                <div>
                  {label}: {value}
                </div>
              }
            >
              <label
                key={legend.id}
                className="flex items-center cursor-pointer"
              >
                <Checkbox
                  value={chartIdsSet.has(legend.id)}
                  checkboxColor={legend.color}
                  onChange={() => {
                    trackMMMEvent({
                      component:
                        ModelInsightsTrackedComponent.ALL_CHANNEL_GENERAL_INSIGHTS_GRAPH,
                      action: "onClick",
                      actionInfo: "Toggle dataset visibility",
                    });

                    const cloned = clone(chartIdsSet);

                    if (cloned.has(legend.id)) {
                      cloned.delete(legend.id);
                    } else {
                      cloned.add(legend.id);
                    }

                    setChartIds(Array.from(cloned));
                  }}
                />
                <div className="truncate cursor-pointer text-sm">
                  {label}: <span className="font-semibold">{value}</span>
                </div>
              </label>
            </GenericTooltip>
          );
        })}
      </div>
      <div className="flex items-center mb-2">
        <TertiaryButton
          className="flex items-center space-x-1.5 font-semibold px-2"
          size="sm"
          onClick={() => {
            trackMMMEvent({
              component:
                ModelInsightsTrackedComponent.ALL_CHANNEL_GENERAL_INSIGHTS_GRAPH,
              action: "onClick",
              actionInfo: "Toggle show all / collapse channels in legend",
            });
            setShowAll(!showAll);
          }}
        >
          {!showAll ? (
            <>
              <span>SHOW ALL</span>
              {splitLegend.length - gridColCount <= 0 ? null : (
                <span
                  className={clsx(
                    "bg-cosmic-3 text-v2-primary text-3xs",
                    "rounded-full h-5 w-5 flex-center inline-flex border-[0.5px] border-[rgba(0,0,0,0.1)]",
                  )}
                >
                  +{splitLegend.length - gridColCount}
                </span>
              )}
            </>
          ) : (
            "COLLAPSE"
          )}
        </TertiaryButton>
        <div className="border-l w-0.5 h-3"></div>
        <TertiaryButton
          className="font-semibold px-2"
          size="sm"
          onClick={() => {
            trackMMMEvent({
              component:
                ModelInsightsTrackedComponent.ALL_CHANNEL_GENERAL_INSIGHTS_GRAPH,
              action: "onClick",
              actionInfo: "Deselect all datasets",
            });

            setChartIds([]);
          }}
        >
          DESELECT ALL
        </TertiaryButton>
      </div>
    </div>
  );
}
